import type { RouteRecordRaw } from "vue-router";
import AppLayout from "@/layouts/AppLayout.vue";
import RouterBypass from "@/layouts/RouterBypass.vue";
import RouteViewComponent from "@/layouts/RouterBypass.vue";
import { managerGuard, orgAdminGuard, validateRouteParams } from "../routerHelpers";

export const managerRoutes: RouteRecordRaw[] = [
  {
    name: "manager",
    path: "/manager",
    component: AppLayout,
    redirect: { name: "manager-dashboard" },
    beforeEnter: managerGuard,
    children: [
      {
        name: "manager-dashboard",
        path: "dashboard",
        component: () => import("@/pages/manager/dashboard/ManagerDashboard.vue"),
      },
      {
        name: "performance-center",
        path: "performance",
        component: () =>
          import("@/pages/manager/performance/PerformanceCenterPage.vue"),
      },
      {
        name: "lakes",
        path: "lakes",
        component: RouteViewComponent,
        children: [
          {
            name: "lakes-list",
            path: "",
            component: () => import("@/pages/manager/lakes/LakesPage.vue"),
          },
          {
            name: "lake-manage",
            path: ":lakeId/manage",
            component: () =>
              import("@/pages/manager/lakes/LakeManagementPage.vue"),
            props: true,
            beforeEnter: (to, from, next) => {
              if (!validateRouteParams(to)) {
                return next({ name: "404" });
              }
              next();
            },
          },
          {
            name: "lake-pricing",
            path: ":lakeId/pricing",
            component: () =>
              import("@/pages/manager/lakes/LakePricingPage.vue"),
            props: true,
            beforeEnter: (to, from, next) => {
              if (!validateRouteParams(to)) {
                return next({ name: "404" });
              }
              next();
            },
          },
          {
            name: "lake-communications",
            path: ":lakeId/communications",
            component: () =>
              import("@/pages/manager/lakes/LakeCommunicationsPage.vue"),
            props: true,
            beforeEnter: (to, from, next) => {
              if (!validateRouteParams(to)) {
                return next({ name: "404" });
              }
              next();
            },
          },
          {
            name: "lake-boost",
            path: ":lakeId/boost",
            component: () => import("@/pages/manager/lakes/LakeBoostPage.vue"),
            props: true,
            beforeEnter: (to, from, next) => {
              if (!validateRouteParams(to)) {
                return next({ name: "404" });
              }
              next();
            },
          },
          {
            name: "lake-fishing-spots",
            path: "fishing-spots",
            component: () =>
              import("@/pages/manager/lakes/FishingSpotsPage.vue"),
          },
        ],
      },
      {
        name: "reservations",
        path: "reservations",
        component: () =>
          import("@/pages/manager/reservations/ReservationsPage.vue"),
      },
      {
        name: "checkin",
        path: "checkin",
        component: RouteViewComponent,
        children: [
          {
            name: "checkin-scan",
            path: "scan",
            component: () => import("@/pages/manager/checkin/ScanPage.vue"),
          },
          {
            name: "checkin-presences",
            path: "presences",
            component: () => import("@/pages/manager/checkin/PresencesPage.vue"),
          },
          {
            name: "checkin-history",
            path: "history",
            component: () => import("@/pages/manager/checkin/HistoryPage.vue"),
          },
          {
            name: "checkin-settings",
            path: "settings",
            component: () =>
              import("@/pages/manager/checkin/CheckinSettingsPage.vue"),
          },
        ],
      },
      {
        name: "reservation-options",
        path: "reservation-options",
        component: () =>
          import("@/pages/manager/reservations/ReservationOptionsPage.vue"),
      },
      {
        name: "campaigns",
        path: "campaigns",
        component: RouteViewComponent,
        children: [
          {
            name: "campaigns-list",
            path: "",
            component: () =>
              import("@/pages/manager/campaigns/CampaignsListPage.vue"),
          },
          {
            name: "campaign-create",
            path: "create",
            component: () =>
              import("@/pages/manager/campaigns/CampaignWizardPage.vue"),
          },
          {
            name: "campaign-detail",
            path: ":campaignId",
            component: () =>
              import("@/pages/manager/campaigns/CampaignDetailPage.vue"),
            props: true,
          },
        ],
      },
      {
        // Chaque section de paramètres a désormais sa propre route et sa propre
        // entrée de menu. Cette route conserve la compatibilité des liens en
        // circulation (`/manager/settings?tab=billing`, notamment posés par
        // d'anciennes redirections et par des emails).
        name: "settings",
        path: "settings",
        redirect: (to) => {
          const tab = typeof to.query.tab === "string" ? to.query.tab : null;
          // `tab` est absorbé par le segment de chemin, mais le RESTE de la
          // query doit survivre : le tunnel d'inscription passe par
          // `{ name: 'settings', query: { tab: 'billing', plan: X } }` et
          // BillingTab a besoin de `plan` pour lancer le paiement.
          const { tab: _tab, ...query } = to.query;
          const SECTIONS = [
            "account",
            "notifications",
            "email-branding",
            "google-reviews",
            "billing",
            "security",
          ];
          const section = tab && SECTIONS.includes(tab) ? tab : "account";
          return { name: `settings-${section}`, query };
        },
      },
      // Une route explicite par section plutôt qu'un segment paramétré : les
      // cibles du menu se résolvent alors littéralement, ce que vérifie
      // tests/navigation-consistency.test.ts.
      ...(
        [
          "account",
          "notifications",
          "email-branding",
          "google-reviews",
          "billing",
          "security",
        ] as const
      ).map((section) => ({
        name: `settings-${section}`,
        path: `settings/${section}`,
        component: () => import("@/pages/settings/Settings.vue"),
        props: { section },
        // Facturation et sécurité sont des réglages au niveau organisation :
        // réservés à l'admin, même par accès direct à l'URL.
        ...(section === "billing" || section === "security"
          ? { beforeEnter: orgAdminGuard }
          : {}),
      })),
      {
        // La page « Modules » dupliquait le menu : elle activait le check-in,
        // les campagnes et les SMS, alors que chacun a sa propre section.
        // Chaque activation a rejoint sa fonctionnalité ; le lien historique
        // mène désormais aux réglages du check-in, son contenu le plus spécifique.
        name: "modules-settings",
        path: "settings/modules",
        redirect: { name: "checkin-settings" },
      },
      {
        name: "sms-settings",
        path: "settings/sms",
        component: () =>
          import("@/pages/manager/settings/SmsSettingsPage.vue"),
      },
      {
        name: "preferences",
        path: "preferences",
        redirect: { name: "settings" },
      },
      {
        name: "users",
        path: "users",
        // Administration des comptes : réservée à l'admin d'organisation.
        beforeEnter: orgAdminGuard,
        component: () => import("@/pages/users/UsersPage.vue"),
      },
      {
        name: "team",
        path: "team",
        // Gestion de l'équipe : réservée à l'admin d'organisation.
        beforeEnter: orgAdminGuard,
        component: () => import("@/pages/manager/team/TeamPage.vue"),
      },
      {
        name: "subscription",
        path: "subscription",
        redirect: { name: "settings" },
      },
      {
        name: "billing",
        path: "billing",
        redirect: { name: "settings", query: { tab: "billing" } },
      },
      {
        name: "payout-settings",
        path: "payout-settings",
        redirect: { name: "settings", query: { tab: "billing" } },
      },
      {
        name: "faq",
        path: "faq",
        component: () => import("@/pages/faq/FaqPage.vue"),
      },
      {
        name: "search-results",
        path: "search-results",
        component: () => import("@/pages/search-results/SearchResultsPage.vue"),
      },
    ],
  },
  {
    path: "/manager/checkin/fullscreen",
    component: RouterBypass,
    beforeEnter: managerGuard,
    children: [
      {
        name: "checkin-fullscreen",
        path: "",
        component: () =>
          import("@/pages/manager/checkin/FullscreenScanPage.vue"),
      },
    ],
  },
];
